import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";

import { AlternativeCropSearchResolver } from "@services/alternativecrop-search-resolver.service";
import { ClassificationReportResolver } from "@services/classification-report-resolver.service";
import { DurationSearchResolver } from "@services/duration-search-resolver.service";
import { GroupSearchResolver } from "@services/group-search-resolver.service";
import { GrowthHabitSearchResolver } from "@services/growthhabit-search-resolver.service";
import { ImageSearchResolver } from "@services/image-search-resolver.service";
import { NoxiousInvasiveSearchResolver } from "@services/noxious-invasive-search-resolver.service";
import { PlantProfileResolver } from "@services/plant-profile-resolver.service";
import { RaritySearchResolver } from "@services/rarity-search-resolver.service";
import { StateSearchResolver } from "@services/state-search-resolver.service";
import { WetlandSearchResolver } from "@services/wetland-search-resolver.service";

import { plantProfileRedirectGuard } from "./common/guards/plant-profile-redirect.guard";
import { HomeComponent } from "./home/home.component";
import { PageNotFoundComponent } from "./page-not-found/page-not-found.component";
import { SearchResultsComponent } from "./search-results/search-results.component";

const routes: Routes = [
  {
    path: "",
    component: HomeComponent,
    title: "USDA Plants Database",
    data: { breadcrumbLabel: "Home" }
  },
  {
    path: "downloads",
    title: "USDA Plants Database Downloads",
    loadComponent: () =>
      import("./downloads/downloads.component").then((mod) => mod.DownloadsComponent),
    data: { breadcrumbLabel: "Downloads" }
  },
  {
    path: "help",
    loadComponent: () => import("./help/help.component").then((mod) => mod.HelpComponent),
    title: "USDA Plants Database Help",
    data: { breadcrumbLabel: "Help" }
  },
  {
    path: "basic-search-results",
    loadComponent: () =>
      import("./search-results/search-results.component").then((mod) => mod.SearchResultsComponent),
    title: "USDA Plants Database Basic Search Results",
    data: { breadcrumbLabel: "Basic Search Results" }
  },
  {
    path: "characteristics-search",
    loadComponent: () =>
      import("./characteristic-search-results/characteristic-search-results.component").then(
        (mod) => mod.CharacteristicSearchResultsComponent
      ),
    title: "USDA Plants Database Characteristics Search",
    data: { breadcrumbLabel: "Characteristics Search" }
  },
  {
    path: "characteristics-search-results",
    component: SearchResultsComponent,
    title: "USDA Plants Database Characteristics Search Results",
    data: { breadcrumbLabel: "Characteristics Search" }
  },
  {
    path: "duration-search",
    loadComponent: () =>
      import("./search/duration-search/duration-search.component").then(
        (mod) => mod.DurationSearchComponent
      ),
    title: "USDA Plants Database Duration Search",
    data: { breadcrumbLabel: "Duration Search" },
    resolve: { criteria: DurationSearchResolver },
    children: [
      {
        path: "duration-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database Duration Search Results",
        data: { breadcrumbLabel: "Duration Search Results" }
      }
    ]
  },
  {
    path: "fact-sheet-plant-guide-search-results",
    component: SearchResultsComponent,
    title: "USDA Plants Database Fact Sheet Plant Guide Search Results",
    data: { breadcrumbLabel: "Fact Sheets & Plant Guides" }
  },
  {
    path: "group-search",
    loadComponent: () =>
      import("./search/group-search/group-search.component").then(
        (mod) => mod.GroupSearchComponent
      ),
    title: "USDA Plants Database Group Search",
    data: { breadcrumbLabel: "Group Search" },
    resolve: { criteria: GroupSearchResolver },
    children: [
      {
        path: "group-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database Group Search Results",
        data: { breadcrumbLabel: "Group Search Results" }
      }
    ]
  },
  {
    path: "growth-habit-search",
    loadComponent: () =>
      import("./search/growthhabit-search/growthhabit-search.component").then(
        (mod) => mod.GrowthHabitSearchComponent
      ),
    title: "USDA Plants Database Growth Habit Search",
    data: { breadcrumbLabel: "Growth Habit Search" },
    resolve: { criteria: GrowthHabitSearchResolver },
    children: [
      {
        path: "growth-habit-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database Growth Habit Search Results",
        data: { breadcrumbLabel: "Growth Habit Search Results" }
      }
    ]
  },
  {
    path: "image-search",
    loadComponent: () =>
      import("./search/image-search/image-search.component").then(
        (mod) => mod.ImageSearchComponent
      ),
    title: "USDA Plants Database Image Search",
    data: { breadcrumbLabel: "Image Search" },
    resolve: { criteria: ImageSearchResolver },
    children: [
      {
        path: "image-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database Image Search Results",
        data: { breadcrumbLabel: "Image Search Results" }
      }
    ]
  },
  {
    path: "noxious-invasive-search",
    loadComponent: () =>
      import("./search/noxious-invasive-search/noxious-invasive-search.component").then(
        (mod) => mod.NoxiousInvasiveSearchComponent
      ),
    title: "USDA Plants Database Noxious and Invasive Search",
    data: { breadcrumbLabel: "Invasive & Noxious Search" },
    resolve: { criteria: NoxiousInvasiveSearchResolver },
    children: [
      {
        path: "noxious-invasive-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database Noxious and Invasive Search Results",
        data: { breadcrumbLabel: "Invasive & Noxious Search Results" }
      }
    ]
  },
  {
    path: "rarity-search",
    loadComponent: () =>
      import("./search/rarity-search/rarity-search.component").then(
        (mod) => mod.RaritySearchComponent
      ),
    title: "USDA Plants Database Rarity Search",
    data: { breadcrumbLabel: "Rarity Search" },
    resolve: { criteria: RaritySearchResolver },
    children: [
      {
        path: "rarity-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database Rarity Search Results",
        data: { breadcrumbLabel: "Rarity Search Results" }
      }
    ]
  },
  {
    path: "state-search",
    loadComponent: () =>
      import("./search/state-search/state-search.component").then(
        (mod) => mod.StateSearchComponent
      ),
    title: "USDA Plants Database State Search",
    data: { breadcrumbLabel: "State Search" },
    resolve: { criteria: StateSearchResolver },
    children: [
      {
        path: "state-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database State Search Results",
        data: { breadcrumbLabel: "State Search Results" }
      }
    ]
  },
  {
    path: "wetland-search",
    loadComponent: () =>
      import("./search/wetland-search/wetland-search.component").then(
        (mod) => mod.WetlandSearchComponent
      ),
    title: "USDA Plants Database Wetland Search",
    data: { breadcrumbLabel: "Wetland Search" },
    resolve: { criteria: WetlandSearchResolver },
    children: [
      {
        path: "wetland-search-results",
        component: SearchResultsComponent,
        title: "USDA Plants Database Wetland Search Results",
        data: { breadcrumbLabel: "Wetland Search Results" }
      }
    ]
  },
  {
    path: "partners",
    loadComponent: () =>
      import("./partners/partners.component").then((mod) => mod.PartnersComponent),
    title: "USDA Plants Database Partners",
    data: { breadcrumbLabel: "Partners" }
  },
  {
    path: "plant-profile",
    canActivate: [plantProfileRedirectGuard],
    loadComponent: () =>
      import("./plant-profile/plant-profile.component").then((mod) => mod.PlantProfileComponent),
    title: "USDA Plants Database Plant Profile",
    data: { breadcrumbLabel: "Plant Profile" },
    resolve: { plant: PlantProfileResolver }
  },
  {
    path: "plant-profile/:id",
    loadComponent: () =>
      import("./plant-profile/plant-profile.component").then((mod) => mod.PlantProfileComponent),
    data: { breadcrumbLabel: "Plant Profile" },
    resolve: { plant: PlantProfileResolver },
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./plant-profile/profile-general/profile-general.component").then(
            (mod) => mod.ProfileGeneralComponent
          ),
        title: "USDA Plants Database Plant Profile General",
        data: { breadcrumbLabel: "General" }
      },
      {
        path: "related-links",
        loadComponent: () =>
          import("./plant-profile/profile-related-links/profile-relatedlinks.component").then(
            (mod) => mod.PlantProfileRelatedLinksComponent
          ),
        title: "USDA Plants Database Plant Profile Related Links",
        data: { breadcrumbLabel: "Related Links" }
      },
      {
        path: "images",
        loadComponent: () =>
          import("./plant-profile/profile-images/profile-images.component").then(
            (mod) => mod.PlantProfileImagesComponent
          ),
        title: "USDA Plants Database Plant Profile Images",
        data: { breadcrumbLabel: "Images" }
      },
      {
        path: "synonyms",
        loadComponent: () =>
          import("./plant-profile/profile-synonyms/profile-synonyms.component").then(
            (mod) => mod.PlantProfileSynonymsComponent
          ),
        title: "USDA Plants Database Plant Profile Synonyms",
        data: { breadcrumbLabel: "Synonyms" }
      },
      {
        path: "wildlife",
        loadComponent: () =>
          import("./plant-profile/profile-wildlife/profile-wildlife.component").then(
            (mod) => mod.PlantProfileWildlifeComponent
          ),
        title: "USDA Plants Database Plant Profile Wildlife",
        data: { breadcrumbLabel: "Wildlife" }
      },
      {
        path: "sources",
        loadComponent: () =>
          import("./plant-profile/profile-documentation/profile-documentation.component").then(
            (mod) => mod.PlantProfileDocumentationComponent
          ),
        title: "USDA Plants Database Plant Profile Sources",
        data: { breadcrumbLabel: "Sources" }
      },
      {
        path: "characteristics",
        loadComponent: () =>
          import("./plant-profile/profile-characteristics/profile-characteristics.component").then(
            (mod) => mod.PlantProfileCharacteristicsComponent
          ),
        title: "USDA Plants Database Plant Profile Characteristics",
        data: { breadcrumbLabel: "Characteristics" }
      },
      {
        path: "subordinate-taxa",
        loadComponent: () =>
          import("./plant-profile/profile-subordinatetaxa/profile-subordinatetaxa.component").then(
            (mod) => mod.PlantProfileSubordinateTaxaComponent
          ),
        title: "USDA Plants Database Plant Profile Subordinate Taxa",
        data: { breadcrumbLabel: "Subordinate Taxa" }
      },
      {
        path: "wetland",
        loadComponent: () =>
          import("./plant-profile/profile-wetland/profile-wetland.component").then(
            (mod) => mod.PlantProfileWetlandComponent
          ),
        title: "USDA Plants Database Plant Profile Wetland",
        data: { breadcrumbLabel: "Wetland" }
      },
      {
        path: "rarity",
        loadComponent: () =>
          import("./plant-profile/profile-legal-status/profile-legal-status.component").then(
            (mod) => mod.PlantProfileLegalStatusComponent
          ),
        title: "USDA Plants Database Plant Profile Legal Status",
        data: { breadcrumbLabel: "Legal Status" }
      },
      {
        path: "noxious-invasive",
        loadComponent: () =>
          import("./plant-profile/profile-noxious-invasive/profile-invasivenoxious.component").then(
            (mod) => mod.PlantProfileInvasiveNoxiousComponent
          ),
        title: "USDA Plants Database Plant Profile Noxious and Invasive",
        data: { breadcrumbLabel: "Noxious & Invasive" }
      },
      {
        path: "ethnobotany",
        loadComponent: () =>
          import("./plant-profile/profile-ethnobotany/profile-ethnobotany.component").then(
            (mod) => mod.PlantProfileEthnobotanyComponent
          ),
        title: "USDA Plants Database Plant Profile Ethnobotany",
        data: { breadcrumbLabel: "Ethnobotany" }
      },
      {
        path: "pollinators",
        loadComponent: () =>
          import("./plant-profile/profile-pollinator/profile-pollinator.component").then(
            (mod) => mod.PlantProfilePollinatorComponent
          ),
        title: "USDA Plants Database Plant Profile Pollinators",
        data: { breadcrumbLabel: "Pollinators" }
      }
    ]
  },
  {
    path: "team",
    loadComponent: () => import("./teams/team.component").then((mod) => mod.TeamComponent),
    title: "USDA Plants Database Team",
    data: { breadcrumbLabel: "Team" }
  },
  {
    path: "tools",
    loadComponent: () => import("./tools/tools.component").then((mod) => mod.ToolsComponent),
    title: "USDA Plants Database Tools",
    data: { breadcrumbLabel: "Related Tools" }
  },
  {
    path: "topics",
    loadComponent: () => import("./topics/topics.component").then((mod) => mod.TopicsComponent),
    title: "USDA Plants Database Topics",
    data: { breadcrumbLabel: "Topics" }
  },

  {
    path: "cover-crop-plants",
    loadComponent: () =>
      import("./cover-crops/cover-crop-plants.component").then(
        (mod) => mod.CoverCropPlantsComponent
      ),
    title: "USDA Plants Database Cover Crops",
    data: { breadcrumbLabel: "Cover Crops" }
  },
  {
    path: "culturally-significant-plants",
    loadComponent: () =>
      import("./culturally-significant-plants/culturally-significant-plants.component").then(
        (mod) => mod.CulturallySignificantPlantsComponent
      ),
    title: "USDA Plants Database Culturally Significant Plants",
    data: { breadcrumbLabel: "Culturally Significant" }
  },
  {
    path: "interactive-id-keys",
    loadComponent: () =>
      import("./interactive-id-keys/interactive-id-keys.component").then(
        (mod) => mod.PlantInteractiveIdKeysComponent
      ),

    title: "USDA Plants Database Interactive ID Keys",
    data: { breadcrumbLabel: "PLANTS Interactive ID Keys" }
  },
  {
    path: "classification/:id",
    loadComponent: () =>
      import("./classification/classification-report/classification-report.component").then(
        (mod) => mod.ClassificationReportComponent
      ),
    title: "USDA Plants Database Classification ID Report",
    data: { breadcrumbLabel: "Classification Report" },
    resolve: { plant: ClassificationReportResolver }
  },
  {
    path: "release-notes",
    loadComponent: () =>
      import("./release-notes/previous-release-notes/previous-release-notes.component").then(
        (mod) => mod.PreviousReleaseNotesComponent
      ),
    title: "USDA Plants Database Release Notes",
    data: { breadcrumbLabel: "Previous Release Notes" }
  },
  {
    path: "usage",
    loadComponent: () => import("./usage/usage.component").then((mod) => mod.UsageComponent),
    title: "USDA Plants Database Usage",
    data: { breadcrumbLabel: "Usage" }
  },
  {
    path: "status",
    loadComponent: () =>
      import("./status-check/status-check.component").then((mod) => mod.StatusCheckComponent),
    title: "USDA Plants Database Status",
    data: { breadcrumbLabel: "Status" }
  },
  { path: "home", redirectTo: "", pathMatch: "full" },
  { path: "**", component: PageNotFoundComponent }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: "reload",
      scrollPositionRestoration: "enabled"
    })
  ],
  exports: [RouterModule],
  providers: [
    PlantProfileResolver,
    ClassificationReportResolver,
    AlternativeCropSearchResolver,
    StateSearchResolver,
    GroupSearchResolver,
    DurationSearchResolver,
    GrowthHabitSearchResolver,
    RaritySearchResolver,
    WetlandSearchResolver,
    NoxiousInvasiveSearchResolver,
    ImageSearchResolver
  ]
})
export class AppRoutingModule {}
